import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, ChevronRight, FolderOpen, Zap } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../lib/api';
import { ConfirmDialog, CasesListSkeleton, PageHeader } from '../components/ui';
import { toast } from '../stores/toast-store';

export function CasesList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [caseToDelete, setCaseToDelete] = useState<{ id: string; title: string } | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['cases', { status: statusFilter }],
    queryFn: () => api.cases.list({ status: statusFilter || undefined, limit: 50 }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.cases.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Case deleted', 'The case has been permanently deleted.');
      setCaseToDelete(null);
    },
    onError: (error) => {
      toast.error('Failed to delete', error instanceof Error ? error.message : 'An error occurred');
    },
  });

  const filteredCases = data?.data.filter(
    (c) =>
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.condition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteClick = (id: string, title: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCaseToDelete({ id, title });
  };

  const handleConfirmDelete = () => {
    if (caseToDelete) {
      deleteMutation.mutate(caseToDelete.id);
    }
  };

  const handleRowClick = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        breadcrumbs={[{ label: 'Cases', icon: FolderOpen }]}
        title="Cases"
        description={data ? `${filteredCases?.length || 0} of ${data.pagination.total} cases` : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/andy-pro" className="btn-secondary">
              <Zap className="mr-1 h-4 w-4" />
              Quick Analysis
            </Link>
            <Link to="/cases/new" className="btn-primary">
              <Plus className="mr-1 h-4 w-4" />
              New Case
            </Link>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search cases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-8"
            aria-label="Search cases"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-36"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="ACTIVE">Active</option>
          <option value="ARCHIVED">Archived</option>
        </select>
      </div>

      {/* Cases Table */}
      {isLoading ? (
        <CasesListSkeleton rows={5} />
      ) : filteredCases && filteredCases.length > 0 ? (
        <div className="card p-0 overflow-hidden">
          <table className="w-full" role="table" aria-label="Cases list">
            <caption className="sr-only">List of patient cases with status and actions</caption>
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th scope="col" className="text-left py-2 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Case
                </th>
                <th scope="col" className="text-left py-2 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide hidden md:table-cell">
                  Created
                </th>
                <th scope="col" className="text-left py-2 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Status
                </th>
                <th scope="col" className="w-16">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredCases.map((caseItem) => (
                <tr
                  key={caseItem.id}
                  onClick={() => handleRowClick(caseItem.id)}
                  className="hover:bg-primary-50 cursor-pointer transition-colors group"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleRowClick(caseItem.id)}
                  role="row"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-md group-hover:text-primary-700">
                          {caseItem.title}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-md">
                          {caseItem.condition}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <div className="text-xs text-gray-500">
                      <span title={format(new Date(caseItem.createdAt), 'PPpp')}>
                        {formatDistanceToNow(new Date(caseItem.createdAt), { addSuffix: true })}
                      </span>
                      <span className="mx-1">by</span>
                      <span className="text-gray-700">{caseItem.createdBy.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                        caseItem.status === 'ACTIVE'
                          ? 'bg-yellow-100 text-yellow-800'
                          : caseItem.status === 'ARCHIVED'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {caseItem.status === 'ACTIVE' ? 'Active' : caseItem.status === 'ARCHIVED' ? 'Archived' : 'Draft'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => handleDeleteClick(caseItem.id, caseItem.title, e)}
                        className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                        aria-label={`Delete case: ${caseItem.title}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card py-12 text-center">
          <Search className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">
            {searchTerm ? 'No cases match your search' : 'No cases found'}
          </p>
          <Link to="/cases/new" className="btn-primary mt-3">
            Create a new case
          </Link>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={caseToDelete !== null}
        onClose={() => setCaseToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Case"
        message={`Are you sure you want to delete "${caseToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
